import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UserGroupIcon,
  BookOpenIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  CalendarIcon,
  ClipboardDocumentIcon,
  EnvelopeIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  XMarkIcon,
  Bars3Icon,
  BriefcaseIcon,
  ClockIcon,
  DocumentTextIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { authService } from '../services/auth';
import { canSeeNavigationItem } from '../utils/permissions';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [isLoadingOrg, setIsLoadingOrg] = useState(true);
  const [copied, setCopied] = useState(false);
  const location = useLocation();
  const user = authService.getUser();
  const userRole = user?.role;

  // Fetch organization ID on component mount
  useEffect(() => {
    fetchOrganizationId();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchOrganizationId = async () => {
    try {
      setIsLoadingOrg(true);
      // Use organization_id from user context instead of calling /me endpoint
      if (user && user.organization_id) {
        setOrganizationId(user.organization_id);
        console.log('Organization ID fetched from user context:', user.organization_id);
      } else {
        console.log('No organization ID in user context');
      }
    } catch (error) {
      console.error('Failed to get organization ID:', error);
    } finally {
      setIsLoadingOrg(false);
    }
  };

  const copyToClipboard = async () => {
    if (organizationId) {
      try {
        await navigator.clipboard.writeText(organizationId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy: ', err);
      }
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'Enquiries', href: '/enquiries', icon: BookOpenIcon },
    { name: 'Students', href: '/students', icon: UserGroupIcon },
    { name: 'Batches', href: '/batches', icon: AcademicCapIcon },
    { name: 'Enrollments', href: '/enrollments', icon: ClipboardDocumentIcon },
    { name: 'Fees', href: '/fees', icon: CurrencyDollarIcon },
    { name: 'Payments', href: '/payments', icon: CreditCardIcon },
    { name: 'Workshops', href: '/workshops', icon: CalendarIcon },
    { name: 'Tasks', href: '/tasks', icon: ClipboardDocumentIcon },
    { name: 'Tests', href: '/tests', icon: DocumentTextIcon },
    { name: 'Top Performers', href: '/top-performers', icon: TrophyIcon },
    { name: 'Employees', href: '/employees', icon: BriefcaseIcon },
    { name: 'Employee Attendance', href: '/employee-attendance', icon: ClockIcon },
    { name: 'Student Attendance', href: '/student-attendance', icon: ClockIcon },
    { name: 'Invitations', href: '/invitations', icon: EnvelopeIcon },
  ];

  // Filter navigation items based on user permissionsc
  const filteredNavigation = navigation.filter(item => 
    canSeeNavigationItem(userRole, item.name)
  );

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  // Dispatch event when sidebar state changes
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('sidebarToggle', { 
      detail: { isCollapsed } 
    }));
  }, [isCollapsed]);

  return (
    <>
      {/* Desktop Sidebar - Hidden on mobile */}
      <div className={`fixed inset-y-0 left-0 z-50 bg-gray-900 shadow-lg transition-all duration-300 ease-in-out hidden md:block ${
        isCollapsed ? 'w-28' : 'w-64'
      }`}>
        {/* Logo Section */}
        <div className={`flex flex-col px-4 pt-3 ${isCollapsed ? 'items-center' : ''}`}>
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              {isCollapsed ? (
                <img 
                  src="/icon.ico" 
                  alt="Neurons School Icon" 
                  className="h-12 w-12 rounded-xl"
                  title="Neurons School"
                />
              ) : (
                <img 
                  src="/neurons-logo.avif" 
                  alt="Neurons School Logo" 
                  className="h-10 w-auto"
                />
              )}
            </Link>
            {!isCollapsed && (
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-1 rounded-md text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          
          {/* Organization ID - Below Logo */}
          {!isCollapsed && (
            <div className="mt-3 mb-2">
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-2 px-2 py-1.5 bg-gray-700/50 rounded-lg border border-gray-600/30">
                  <span className="text-xs font-medium text-gray-300">ID:</span>
                  {isLoadingOrg ? (
                    <span className="text-xs text-yellow-400 font-mono">Loading...</span>
                  ) : organizationId ? (
                    userRole === 'admin' ? (
                      <div className="flex items-center">
                        <Link
                          to="/organization"
                          className="text-xs font-mono text-blue-400 hover:text-blue-300 transition-all duration-200 font-semibold tracking-wide"
                          title="Click to view organization details"
                        >
                          {organizationId}
                        </Link>
                        <button
                          onClick={copyToClipboard}
                          className="ml-2 p-1 rounded-md text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                          title="Copy Organization ID"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                        {copied && (
                          <span className="ml-2 text-xs text-green-400 font-mono">Copied!</span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <span className="text-xs font-mono text-gray-300 font-semibold tracking-wide">
                          {organizationId}
                        </span>
                        <button
                          onClick={copyToClipboard}
                          className="ml-2 p-1 rounded-md text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                          title="Copy Organization ID"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                        {copied && (
                          <span className="ml-2 text-xs text-green-400 font-mono">Copied!</span>
                        )}
                      </div>
                    )
                  ) : (
                    <span className="text-xs text-red-400 font-mono">Not found</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Separator Line */}
        <div className="border-b border-gray-700"></div>

        {/* Collapse Toggle Button - Only visible when collapsed */}
        {isCollapsed && (
          <div className="flex justify-center mt-2">
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-2 rounded-md text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
              title="Expand Sidebar"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Navigation Items */}
        <nav className="mt-4 px-2">
          <div className="space-y-1">
            {filteredNavigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors group ${
                  isActive(item.href)
                    ? 'text-white bg-blue-600'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                } ${isCollapsed ? 'justify-center' : ''}`}
                title={isCollapsed ? item.name : ''}
              >
                <item.icon className="h-6 w-6 flex-shrink-0" />
                {!isCollapsed && (
                  <span className="ml-3">{item.name}</span>
                )}
              </Link>
            ))}
          </div>
        </nav>

        {/* User Section - Bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700">
          <div className="flex items-center space-x-3">
            <Link
              to="/profile"
              className="flex items-center p-2 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
              title="View Profile"
            >
              <UserIcon className="h-6 w-6" />
            </Link>
            {!isCollapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-300 truncate">
                    {user?.username || 'User'}
                  </p>
                </div>
                <button
                  onClick={authService.logout}
                  className="flex items-center px-2 py-1 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setIsOpen(false)} />
        </div>
      )}

      {/* Mobile Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 shadow-lg transform transition-transform duration-300 ease-in-out md:hidden flex flex-col ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Mobile Header - Fixed at top */}
        <div className="flex flex-col px-4 pt-3 pb-3 bg-gray-900 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center" onClick={() => setIsOpen(false)}>
              <img 
                src="/neurons-logo.avif" 
                alt="Neurons School Logo" 
                className="h-8 w-auto"
              />
            </Link>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-md text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          {/* Organization ID - Below Logo */}
          {organizationId && (
            <div className="mt-3">
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-2 px-2 py-1.5 bg-gray-700/50 rounded-lg border border-gray-600/30">
                  <span className="text-xs font-medium text-gray-300 flex items-center">ID:</span>
                  {isLoadingOrg ? (
                    <span className="text-xs text-yellow-400 font-mono flex items-center">Loading...</span>
                  ) : organizationId ? (
                    userRole === 'admin' ? (
                      <div className="flex items-center">
                        <Link
                          to="/organization"
                          className="text-xs font-mono text-blue-400 hover:text-blue-300 transition-all duration-200 font-semibold tracking-wide flex items-center"
                          title="Click to view organization details"
                          onClick={() => setIsOpen(false)}
                        >
                          {organizationId}
                        </Link>
                        <button
                          onClick={copyToClipboard}
                          className="ml-2 p-1 rounded-md text-gray-300 hover:text-white hover:bg-gray-700 transition-colors flex items-center"
                          title="Copy Organization ID"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                        {copied && (
                          <span className="ml-2 text-xs text-green-400 font-mono flex items-center">Copied!</span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <span className="text-xs font-mono text-gray-300 font-semibold tracking-wide flex items-center">
                          {organizationId}
                        </span>
                        <button
                          onClick={copyToClipboard}
                          className="ml-2 p-1 rounded-md text-gray-300 hover:text-white hover:bg-gray-700 transition-colors flex items-center"
                          title="Copy Organization ID"
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                        {copied && (
                          <span className="ml-2 text-xs text-green-400 font-mono flex items-center">Copied!</span>
                        )}
                      </div>
                    )
                  ) : (
                    <span className="text-xs text-red-400 font-mono flex items-center">Not found</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Scrollable Navigation Container - Only the middle section */}
        <div className="flex-1 overflow-y-auto min-h-0 pb-4">
          <nav className="px-2 py-4">
            <div className="space-y-1">
              {filteredNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isActive(item.href)
                      ? 'text-white bg-blue-600'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              ))}
            </div>
          </nav>
        </div>

        {/* Mobile User Section - Fixed at bottom */}
        <div className="p-4 border-t border-gray-700 bg-gray-900 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <Link
              to="/profile"
              className="flex items-center p-2 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <UserIcon className="h-5 w-5" />
            </Link>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-gray-300 truncate">
                {user?.username || 'User'}
              </p>
            </div>
            <button
              onClick={authService.logout}
              className="flex items-center px-2 py-1 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Button */}
      <div className="fixed top-4 left-4 z-30 md:hidden">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2.5 rounded-lg bg-gray-800/80 backdrop-blur-sm text-gray-300 hover:text-white hover:bg-gray-700 transition-all duration-200 shadow-lg border border-gray-600/50"
        >
          <Bars3Icon className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile Logo - Right Side (Only visible when sidebar is closed) */}
      <div className={`absolute top-4 right-4 z-40 md:hidden transition-opacity duration-300 ${isOpen ? 'opacity-0' : 'opacity-100'}`}>
        <Link to="/" className="flex items-center">
          <img 
            src="/neurons-logo.avif" 
            alt="Neurons School Logo" 
            className="h-8 w-auto"
          />
        </Link>
      </div>

      {/* Mobile Header Spacer - Prevents content from being hidden behind menu button */}
      <div className="h-16 md:hidden"></div>
    </>
  );
};

export default Navigation; 